import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "antd";
import RAG_API from "../../apis/rag.api";
import { ragModuleOptions } from "../../components/SidebarConstants";
import "./AIRAG.css";

const DEFAULT_MODULE = "advisor-panel";
const DEFAULT_CHAT_TOP_K = 5;
const INITIAL_CHAT_MESSAGE = {
  role: "assistant",
  text: "Hi there. I’m ready to help with your documents.",
  state: "idle",
};

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const pickArrayFromResponse = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  const candidates = [
    payload.results,
    payload.documents,
    payload.matches,
    payload.items,
    payload.data,
    payload.hits,
    payload.output,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  if (isPlainObject(payload.data)) {
    return pickArrayFromResponse(payload.data);
  }

  return [];
};

const buildCorsFallbackResponse = (action, payload) => ({
  action,
  ui_notice:
    "The request was sent successfully and the response is still coming. We will show the live result here as soon as it arrives.",
  request: payload,
});

const getStreamContent = (parsedPayload) => {
  if (!parsedPayload) return "";
  if (typeof parsedPayload === "string") return parsedPayload;
  if (parsedPayload.content) return parsedPayload.content;
  if (parsedPayload.text) return parsedPayload.text;
  if (parsedPayload.answer) return parsedPayload.answer;
  if (parsedPayload.message && parsedPayload.message.content) {
    return parsedPayload.message.content;
  }
  if (
    parsedPayload.choices &&
    parsedPayload.choices[0] &&
    parsedPayload.choices[0].delta &&
    parsedPayload.choices[0].delta.content
  ) {
    return parsedPayload.choices[0].delta.content;
  }

  return "";
};

const normalizeStreamText = (rawText) => {
  if (!rawText) return "";
  if (rawText.indexOf("data:") === -1) return rawText;

  const lines = rawText.split(/\r?\n/);
  let output = "";
  let sawDataLine = false;

  for (let index = 0; index < lines.length; index += 1) {
    const trimmed = lines[index].trim();
    if (!trimmed) continue;

    if (trimmed === "data: [DONE]") {
      sawDataLine = true;
      continue;
    }

    if (trimmed.indexOf("data:") === 0) {
      sawDataLine = true;
      const payloadText = trimmed.replace(/^data:\s*/, "");

      if (!payloadText) {
        continue;
      }

      try {
        const parsed = JSON.parse(payloadText);
        const content = getStreamContent(parsed);
        if (content) {
          output += content;
        }
      } catch (error) {
        output += payloadText;
      }
    }
  }

  return sawDataLine ? output : rawText;
};

const replaceLastChatMessage = (messages, nextText, nextState) => {
  if (!messages.length) return messages;

  const nextMessages = messages.slice();
  const lastIndex = nextMessages.length - 1;
  const lastMessage = nextMessages[lastIndex];

  nextMessages[lastIndex] = {
    ...lastMessage,
    text: nextText,
    streaming: nextState === "streaming",
    state: nextState,
  };

  return nextMessages;
};

const AIRAG = () => {
  const [ingestForm, setIngestForm] = useState({
    name: "",
    text: "",
    module: DEFAULT_MODULE,
  });
  const [searchForm, setSearchForm] = useState({
    query: "",
    module: DEFAULT_MODULE,
    top_k: DEFAULT_CHAT_TOP_K,
  });
  const [activeTab, setActiveTab] = useState("ingest");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responseState, setResponseState] = useState(null);
  const [errorState, setErrorState] = useState("");
  const [chatForm, setChatForm] = useState({
    query: "",
    module: DEFAULT_MODULE,
  });
  const [chatMessages, setChatMessages] = useState([INITIAL_CHAT_MESSAGE]);
  const [chatErrorState, setChatErrorState] = useState("");
  const [isChatSubmitting, setIsChatSubmitting] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const chatEndRef = useRef(null);
  const chatSessionRef = useRef(0);

  const groupedModuleOptions = useMemo(() => {
    const groups = {};

    ragModuleOptions.forEach((option) => {
      const groupName = option.group || "Modules";
      if (!groups[groupName]) {
        groups[groupName] = [];
      }

      groups[groupName].push(option);
    });

    return groups;
  }, []);

  useEffect(() => {
    if (chatEndRef.current && chatEndRef.current.scrollIntoView) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [chatMessages, isChatSubmitting]);

  const responseData = responseState || {};
  const responseItems = pickArrayFromResponse(responseData);
  const isCorsResponse = Boolean(responseData.ui_notice);
  const isIngestResponse = responseData.count !== undefined || responseData.items !== undefined;
  const isSearchResponse = responseData.results !== undefined;
  const hasResponse = Boolean(responseState);
  const responseHeading = isIngestResponse
    ? "Document added"
    : isCorsResponse
    ? "Response in progress"
    : isSearchResponse
    ? "Search results"
    : "Recent activity";
  const responseSubheading = isIngestResponse
    ? "Your document was ingested successfully."
    : isCorsResponse
    ? responseData.ui_notice
    : isSearchResponse
    ? "Here are the best matches from your knowledge base."
    : "Use the workspace above to add documents or search your knowledge base.";

  const runIngest = async () => {
    setErrorState("");
    setIsSubmitting(true);

    const payload = {
      documents: [
        {
          name: ingestForm.name.trim(),
          text: ingestForm.text.trim(),
          module: ingestForm.module.trim(),
        },
      ],
    };

    setIngestForm((current) => ({
      ...current,
      name: "",
      text: "",
    }));

    try {
      const response = await RAG_API.ingestDocumentRAG(payload);
      if (!response) {
        setResponseState(buildCorsFallbackResponse("ingest", payload));
        return;
      }

      setResponseState(response);
      setActiveTab("search");
    } catch (error) {
      setResponseState(buildCorsFallbackResponse("ingest", payload));
      setErrorState("");
    } finally {
      setIsSubmitting(false);
    }
  };

  const runSearch = async () => {
    setErrorState("");
    setIsSubmitting(true);

    const payload = {
      query: searchForm.query.trim(),
      module: searchForm.module.trim(),
      top_k: Number(searchForm.top_k) || 5,
    };

    setSearchForm((current) => ({
      ...current,
      query: "",
    }));

    try {
      const response = await RAG_API.searchDocumentsRAG(payload);
      console.log("Received search response:", response);
      if (!response) {
        setResponseState(buildCorsFallbackResponse("search", payload));
        return;
      }

      setResponseState(response);
    } catch (error) {
      setResponseState(buildCorsFallbackResponse("search", payload));
      setErrorState("");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetChat = () => {
    chatSessionRef.current += 1;
    setChatForm({
      query: "",
      module: DEFAULT_MODULE,
    });
    setChatMessages([INITIAL_CHAT_MESSAGE]);
    setChatErrorState("");
    setIsChatSubmitting(false);
  };

  const toggleChat = () => {
    setIsChatOpen((current) => !current);
    setChatErrorState("");
  };

  const runChat = async () => {
    const sessionId = chatSessionRef.current + 1;
    chatSessionRef.current = sessionId;

    const nextQuery = chatForm.query.trim();
    if (!nextQuery) {
      setChatErrorState("Please enter a question first.");
      return;
    }

    setChatErrorState("");
    setIsChatSubmitting(true);
    setIsChatOpen(true);
    setChatForm((current) => ({
      ...current,
      query: "",
    }));

    const userMessage = {
      role: "user",
      text: nextQuery,
      state: "done",
    };
    const assistantMessage = {
      role: "assistant",
      text: "",
      state: "streaming",
      streaming: true,
    };

    setChatMessages((current) => current.concat([userMessage, assistantMessage]));

    try {
      const payload = {
        query: nextQuery,
        module: chatForm.module.trim(),
        top_k: DEFAULT_CHAT_TOP_K,
      };
      const response = await RAG_API.streamSearchDocumentsRAG(payload);
      if (chatSessionRef.current !== sessionId) {
        return;
      }
      if (!response || !response.ok) {
        throw new Error("CORS blocked the chat response.");
      }

      if (!response.body || !response.body.getReader) {
        throw new Error("This browser does not support streamed responses.");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let rawText = "";
      let done = false;

      while (!done) {
        const result = await reader.read();
        const value = result.value;
        done = result.done;

        if (value) {
          rawText += decoder.decode(value, { stream: true });
          const formattedText = normalizeStreamText(rawText);

          if (chatSessionRef.current !== sessionId) {
            return;
          }

          setChatMessages((current) => {
            if (!current.length) return current;
            return replaceLastChatMessage(current, formattedText, "streaming");
          });
        }
      }

      const trailingText = decoder.decode();
      if (trailingText) {
        rawText += trailingText;
      }

      const finalText = normalizeStreamText(rawText).trim();
      if (chatSessionRef.current !== sessionId) {
        return;
      }
      setChatMessages((current) => {
        if (!current.length) return current;
        return replaceLastChatMessage(
          current,
          finalText || "No answer was returned from the stream.",
          "done"
        );
      });
    } catch (error) {
      if (chatSessionRef.current !== sessionId) {
        return;
      }
      setChatErrorState(
        "The request was sent, but the browser could not read the response because of CORS. We will enable the live chat once the backend allows this origin."
      );
      setChatMessages((current) => {
        if (!current.length) return current;
        return replaceLastChatMessage(
          current,
          "I sent your message, but the browser is blocking the live response for now. We will show the answer once CORS is enabled.",
          "error"
        );
      });
    } finally {
      if (chatSessionRef.current === sessionId) {
        setIsChatSubmitting(false);
      }
    }
  };

  return (
    <div className="airag-page">
      <div className="airag-page__backdrop airag-page__backdrop--one" />
      <div className="airag-page__backdrop airag-page__backdrop--two" />

      <section className="airag-shell">
        <header className="airag-hero">
          <div className="airag-hero__eyebrow">AI RAG workspace</div>
          <div className="airag-hero__title-row">
            <div>
              <h1 className="airag-hero__title">Upload and find documents</h1>
              <p className="airag-hero__subtitle">
                Add documents here and quickly search for the information you need in one simple
                place.
              </p>
            </div>
           
          </div>
        </header>

        <div className="airag-layout">
          <section className="airag-panel airag-panel--form">
            <div className="airag-panel__header">
              <div>
                <div className="airag-panel__kicker">Quick actions</div>
                <h2 className="airag-panel__title">
                  {activeTab === "ingest" ? "Document ingest" : "Document search"}
                </h2>
              </div>
              <div className="airag-panel__toggle">
                <button
                  type="button"
                  className={`airag-toggle${activeTab === "search" ? " is-active" : ""}`}
                  onClick={() => setActiveTab("search")}
                >
                  Search
                </button>
                <button
                  type="button"
                  className={`airag-toggle${activeTab === "ingest" ? " is-active" : ""}`}
                  onClick={() => setActiveTab("ingest")}
                >
                  Ingest
                </button>
              </div>
            </div>

            {activeTab === "ingest" ? (
              <div className="airag-form">
                <div className="airag-field">
                  <label className="airag-label" htmlFor="rag-document-name">
                    Document name
                  </label>
                  <input
                    id="rag-document-name"
                    className="airag-input"
                    value={ingestForm.name}
                    onChange={(event) => {
                      const value = event.target.value;
                      setIngestForm((current) => ({
                        ...current,
                        name: value,
                      }));
                    }}
                    placeholder="Enter document name"
                  />
                </div>

                <div className="airag-field">
                  <label className="airag-label" htmlFor="rag-document-module">
                    Module
                  </label>
                  <select
                    id="rag-document-module"
                    className="airag-input"
                    value={ingestForm.module}
                    onChange={(event) => {
                      const value = event.target.value;
                      setIngestForm((current) => ({
                        ...current,
                        module: value,
                      }));
                    }}
                  >
                    {Object.keys(groupedModuleOptions).map((groupName) => (
                      <optgroup key={groupName} label={groupName}>
                        {groupedModuleOptions[groupName].map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                <div className="airag-field">
                  <label className="airag-label" htmlFor="rag-document-text">
                    Document text
                  </label>
                  <textarea
                    id="rag-document-text"
                    className="airag-textarea"
                    rows={10}
                
                    value={ingestForm.text}
                    onChange={(event) => {
                      const value = event.target.value;
                      setIngestForm((current) => ({
                        ...current,
                        text: value,
                      }));
                    }}
                    placeholder="Paste or type document content here"
                  />
                </div>

                <div className="airag-actions">
                  <button
                    type="button"
                    className="airag-button airag-button--primary"
                    onClick={runIngest}
                    disabled={isSubmitting || !ingestForm.name.trim() || !ingestForm.text.trim()}
                  >
                    {isSubmitting ? "Ingesting..." : "Ingest document"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="airag-form">
                <div className="airag-field">
                  <label className="airag-label" htmlFor="rag-query">
                    Query
                  </label>
                  <input
                    id="rag-query"
                    className="airag-input"
                    value={searchForm.query}
                    onChange={(event) => {
                      const value = event.target.value;
                      setSearchForm((current) => ({
                        ...current,
                        query: value,
                      }));
                    }}
                    placeholder="Search your documents"
                  />
                </div>

                <div className="airag-row">
                  <div className="airag-field">
                    <label className="airag-label" htmlFor="rag-search-module">
                      Module
                    </label>
                    <select
                      id="rag-search-module"
                      className="airag-input"
                      value={searchForm.module}
                      onChange={(event) => {
                        const value = event.target.value;
                        setSearchForm((current) => ({
                          ...current,
                          module: value,
                        }));
                      }}
                    >
                      {Object.keys(groupedModuleOptions).map((groupName) => (
                        <optgroup key={groupName} label={groupName}>
                          {groupedModuleOptions[groupName].map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>

                  <div className="airag-field">
                    <label className="airag-label" htmlFor="rag-top-k">
                      Top K
                    </label>
                    <input
                      id="rag-top-k"
                      type="number"
                      min="1"
                      max="20"
                      className="airag-input"
                      value={searchForm.top_k}
                      onChange={(event) => {
                        const value = event.target.value;
                        setSearchForm((current) => ({
                          ...current,
                          top_k: value,
                        }));
                      }}
                    />
                  </div>
                </div>

                <div className="airag-actions">
                  <button
                    type="button"
                    className="airag-button airag-button--primary"
                    onClick={runSearch}
                    disabled={isSubmitting || !searchForm.query.trim()}
                  >
                    {isSubmitting ? "Searching..." : "Search documents"}
                  </button>
                </div>
              </div>
            )}
          </section>

        </div>

        <section className="airag-panel airag-panel--response">
          <div className="airag-panel__header">
            <div>
              <div className="airag-panel__kicker">Activity</div>
              <h2 className="airag-panel__title">{responseHeading}</h2>
            </div>
          </div>

          {errorState ? <div className="airag-alert">{errorState}</div> : null}

          <div className="airag-response-summary">
            <div className="airag-response-summary__content">
              <div className="airag-response-summary__subhead">{responseSubheading}</div>
              {isCorsResponse ? (
                <div className="airag-response-summary__notice">
                  <div className="airag-response-summary__notice-title">Response is still coming</div>
                  <div className="airag-response-summary__notice-body">
                    The request went through. We are still waiting for the API response, and this
                    screen will update as soon as it arrives.
                  </div>
                </div>
              ) : null}
              {isIngestResponse ? (
                <div className="airag-response-summary__stats">
                  <div className="airag-response-summary__stat">
                    <span>Documents</span>
                    <strong>{responseData.count || responseItems.length || 0}</strong>
                  </div>
                  <div className="airag-response-summary__stat">
                    <span>Status</span>
                    <strong>
                      {responseItems[0] && responseItems[0].status ? responseItems[0].status : "Ready"}
                    </strong>
                  </div>
                </div>
              ) : null}
              {isSearchResponse && responseItems.length > 0 ? (
                <div className="airag-response-summary__stats">
                  <div className="airag-response-summary__stat">
                    <span>Matches</span>
                    <strong>{responseItems.length}</strong>
                  </div>
                  <div className="airag-response-summary__stat">
                    <span>Top score</span>
                    <strong>
                      {responseItems[0] && responseItems[0].score !== undefined
                        ? responseItems[0].score.toFixed
                          ? responseItems[0].score.toFixed(3)
                          : responseItems[0].score
                        : "Ready"}
                    </strong>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {responseItems.length > 0 ? (
            <div className="airag-results-grid">
              {responseItems.map((item, index) => {
                const title =
                  (item && item.name) ||
                  (item && item.title) ||
                  (item && item.document_name) ||
                  (item && item.doc_name) ||
                  `Item ${index + 1}`;
                const body =
                  (item && item.text) ||
                  (item && item.content) ||
                  (item && item.snippet) ||
                  (item && item.chunk) ||
                  (item && item.description) ||
                  (item && item.status) ||
                  "";
                const hasScore =
                  item && (item.score !== undefined || item.similarity !== undefined);
                const scoreValue = item
                  ? item.score !== undefined
                    ? item.score
                    : item.similarity
                  : undefined;
                const badgeLabel =
                  item && item.module
                    ? item.module
                    : item && item.updated_at
                    ? item.updated_at
                    : "";

                return (
                  <article className="airag-result-card" key={`${title}-${index}`}>
                    <div className="airag-result-card__top">
                      <h3 className="airag-result-card__title">{title}</h3>
                      {hasScore ? <span className="airag-result-card__score">{scoreValue}</span> : null}
                    </div>

                    {badgeLabel ? <div className="airag-result-card__badge">{badgeLabel}</div> : null}
                    <p className="airag-result-card__body">{body}</p>
                  </article>
                );
              })}
            </div>
          ) : hasResponse ? (
            <div className="airag-empty-state">
              We received your request. Add another document or run a search to continue.
            </div>
          ) : null}
        </section>

        <button
          type="button"
          className={`airag-chat-launcher${isChatOpen ? " is-open" : ""}`}
          onClick={toggleChat}
          aria-label={isChatOpen ? "Close AI RAG chat" : "Open AI RAG chat"}
        >
          <span className="airag-chat-launcher__pulse" />
          <span className="airag-chat-launcher__orb">
            <Icon type="message" />
          </span>
          <span className="airag-chat-launcher__copy">
            <span>Ask AI</span>
            <small>RAG</small>
          </span>
        </button>

        <section className={`airag-chat-panel${isChatOpen ? " is-open" : ""}`}>
          <div className="airag-chat-panel__header">
            <div className="airag-chat-panel__title-wrap">
              <div className="airag-chat-panel__greeting">
                <span className="airag-chat-panel__greeting-icon" role="img" aria-label="waving hand">
                  👋
                </span>
                <span>Hi, I’m your AI RAG assistant</span>
              </div>
              <h2 className="airag-panel__title">Search your knowledge base in a chat</h2>
            </div>
            <button type="button" className="airag-link-button" onClick={resetChat}>
              Reset
            </button>
          </div>

          <div className="airag-chat-panel__subhead">
            Pick a module, ask a question, and watch the response stream in real time.
          </div>

          <div className="airag-chat__thread">
            {chatMessages.map((message, index) => (
              <div
                className={`airag-chat__message${
                  message.role === "user" ? " is-user" : " is-assistant"
                }${message.state === "streaming" ? " is-streaming" : ""}`}
                key={`${message.role}-${index}`}
              >
                <div className="airag-chat__message-meta">
                  {message.role === "user" ? "You" : "AI RAG"}
                </div>
                <div className="airag-chat__message-text">
                  {message.role === "assistant" ? (
                    <div className="airag-chat__markdown">
                        {message.text || (message.state === "streaming" ? "Streaming response..." : "")}
                    </div>
                  ) : (
                    message.text || ""
                  )}
                </div>
              </div>
            ))}
            {isChatSubmitting ? (
              <div className="airag-chat__typing">Streaming answer from the RAG endpoint...</div>
            ) : null}
            <div ref={chatEndRef} />
          </div>

          {chatErrorState ? <div className="airag-alert">{chatErrorState}</div> : null}

          <div className="airag-chat__composer">
            <div className="airag-field">
              <label className="airag-label" htmlFor="rag-chat-module">
                Module
              </label>
              <select
                id="rag-chat-module"
                className="airag-input"
                value={chatForm.module}
                onChange={(event) => {
                  const value = event.target.value;
                  setChatForm((current) => ({
                    ...current,
                    module: value,
                  }));
                }}
              >
                {Object.keys(groupedModuleOptions).map((groupName) => (
                  <optgroup key={groupName} label={groupName}>
                    {groupedModuleOptions[groupName].map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div className="airag-field">
              <label className="airag-label" htmlFor="rag-chat-query">
                Your question
              </label>
              <textarea
                id="rag-chat-query"
                className="airag-textarea airag-chat__textarea"
                rows={4}
                value={chatForm.query}
                onChange={(event) => {
                  const value = event.target.value;
                  setChatForm((current) => ({
                    ...current,
                    query: value,
                  }));
                }}
                placeholder="Ask a question about your documents"
              />
            </div>

            <div className="airag-actions airag-chat__actions">
              <button
                type="button"
                className="airag-button airag-button--primary"
                onClick={runChat}
                disabled={isChatSubmitting || !chatForm.query.trim()}
              >
                {isChatSubmitting ? "Streaming..." : "Ask AI RAG"}
              </button>
            </div>
          </div>
        </section>
      </section>
    </div>
  );
};

export { AIRAG };
export default AIRAG;
